/**
 * Drill level grammar for the kit (S3) — pure §6 positional-level helpers.
 * Level NAMES are registry data (the catalogue's drill.levels entity map);
 * this module only encodes the §6 STRUCTURE those names share:
 *   - depths resolve positionally across entity level lists (fm order_line ==
 *     ra job == depth 3); either entity's name addresses a depth at Group;
 *   - depths 0/1 are site/month by construction;
 *   - each level's row id column (backend services/drill_shape.LEVEL_COLUMNS
 *     plus the constructed Site/Period columns of the shallow depths).
 * SITE_ENTITY mirrors backend services/freshness.SITE_ENTITY — which entity's
 * templates serve each site is platform structure (§6), not metric behaviour.
 */

import type { PathSegment, RegistryDrill, RegistryMetric, Site } from '../../types/metrics'

const SITE_ENTITY: Partial<Record<Site, string>> = { Kirkstall: 'fm', Oracle: 'ra' }

/** The row column that names an id at each §6 level. */
const LEVEL_ID_COLUMNS: Record<string, string> = {
  site: 'Site',
  month: 'Period',
  sales_order: 'SalesOrderNumber',
  customer_order: 'CustomerOrderNo',
}

function drillBlock(metric: RegistryMetric | null): RegistryDrill | null {
  return metric?.drill?.enabled ? metric.drill : null
}

function entitiesFor(block: RegistryDrill, site: Site): string[] {
  if (site !== 'Group') {
    const entity = SITE_ENTITY[site]
    return entity && block.levels[entity] ? [entity] : []
  }
  return Object.keys(block.levels).sort()
}

/** Positional request levels for a site — the first served entity's names
 * address every depth (the backend resolves level names positionally at Group). */
export function requestLevels(metric: RegistryMetric | null, site: Site): string[] {
  const block = drillBlock(metric)
  if (!block) return []
  const entities = entitiesFor(block, site)
  return entities.length ? block.levels[entities[0]] : []
}

/** Every served entity's level name at a depth, deduped ('sales_order',
 * 'customer_order' both live at depth 2 in a Group union). */
function namesAtDepth(metric: RegistryMetric | null, site: Site, depth: number): string[] {
  const block = drillBlock(metric)
  if (!block) return []
  const names: string[] = []
  for (const entity of entitiesFor(block, site)) {
    const name = block.levels[entity][depth]
    if (name && !names.includes(name)) names.push(name)
  }
  return names
}

/** A level name's positional depth across ALL served entity lists, or -1. */
function depthOf(metric: RegistryMetric | null, site: Site, level: string): number {
  const block = drillBlock(metric)
  if (!block) return -1
  for (const entity of entitiesFor(block, site)) {
    const index = block.levels[entity].indexOf(level)
    if (index >= 0) return index
  }
  return -1
}

/** OrderBook's level derivation, carried: the view sits one depth below the
 * last path crumb (empty path → depth 0), clamped to the leaf. Metrics with
 * no drill block probe at 'site' — the backend refuses before level checks. */
export function deriveLevel(
  metric: RegistryMetric | null,
  site: Site,
  path: PathSegment[]
): string {
  const levels = requestLevels(metric, site)
  if (levels.length === 0) return 'site'
  const last = path[path.length - 1]
  const depth = last ? Math.min(depthOf(metric, site, last.level) + 1, levels.length - 1) : 0
  return levels[Math.max(depth, 0)]
}

/** Display label for the depth a request level addresses: distinct entity
 * names joined (Group union), snake_case humanised. */
export function levelLabel(metric: RegistryMetric | null, site: Site, level: string): string {
  const depth = depthOf(metric, site, level)
  const names = depth >= 0 ? namesAtDepth(metric, site, depth) : [level]
  return (names.length ? names : [level]).map((n) => n.replace(/_/g, ' ')).join(' / ')
}

/** The crumb a row click would push, or null when the row cannot descend
 * (leaf level, or no id column for this row's entity at this depth). */
export function rowDescent(
  metric: RegistryMetric | null,
  site: Site,
  level: string,
  row: Record<string, unknown>
): PathSegment | null {
  const levels = requestLevels(metric, site)
  const depth = levels.indexOf(level)
  if (depth < 0 || depth >= levels.length - 1) return null
  for (const name of namesAtDepth(metric, site, depth)) {
    const id = row[LEVEL_ID_COLUMNS[name] ?? '']
    if (id !== undefined && id !== null && id !== '') return { level: name, id: String(id) }
  }
  return null
}
